import { ColorMode, DEFAULT_COLOR_MODE, DEFAULT_THEME, isColorMode, isThemeName, type ThemeName } from '@gr/core/platform/user-preferences/themes';
import type { KeyValueStorage } from '../storage';
import { Observable } from '../store';

// PREF-UC-2: тема и режим выбираются независимо; на <html> ставятся
// data-theme и data-mode, а CSS-переменные темы делают остальное.

const THEME_KEY = 'gr.theme';
const MODE_KEY = 'gr.mode';

export interface ThemeState {
  theme: ThemeName;
  mode: ColorMode;
  /** Применённый режим (system раскрыт в light/dark). */
  resolved: ColorMode.Light | ColorMode.Dark;
}

export class ThemeService {
  readonly state: Observable<ThemeState>;
  private readonly media: MediaQueryList | null;

  constructor(
    private readonly storage: KeyValueStorage,
    private readonly root: HTMLElement | null = typeof document !== 'undefined' ? document.documentElement : null,
  ) {
    this.media = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    const theme = storage.get(THEME_KEY);
    const mode = storage.get(MODE_KEY);
    this.state = new Observable(this.resolve(isThemeName(theme) ? theme : DEFAULT_THEME, isColorMode(mode) ? mode : DEFAULT_COLOR_MODE));
    this.media?.addEventListener('change', () => this.apply(this.state.get().theme, this.state.get().mode));
    this.applyToDocument();
  }

  setTheme(theme: ThemeName): void {
    this.storage.set(THEME_KEY, theme);
    this.apply(theme, this.state.get().mode);
  }

  setMode(mode: ColorMode): void {
    this.storage.set(MODE_KEY, mode);
    this.apply(this.state.get().theme, mode);
  }

  private apply(theme: ThemeName, mode: ColorMode): void {
    this.state.set(this.resolve(theme, mode));
    this.applyToDocument();
  }

  private resolve(theme: ThemeName, mode: ColorMode): ThemeState {
    const resolved = mode === ColorMode.System ? (this.media?.matches ? ColorMode.Dark : ColorMode.Light) : mode;
    return { theme, mode, resolved };
  }

  private applyToDocument(): void {
    if (!this.root) return;
    const { theme, resolved } = this.state.get();
    this.root.dataset.theme = theme;
    this.root.dataset.mode = resolved;
  }
}
